import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { ContactModal } from '@/components/modals/ContactModal';
import { useMarketingTranslations } from '@/hooks/useMarketingTranslations';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { useMarketingImages } from '@/hooks/useMarketingImages';

interface FeatureSectionProps {
  feature: string;
  reverse?: boolean;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({ feature, reverse = false }) => {
  const { getProductFeature, getProductFeatureBullets } = useMarketingTranslations();
  const { getImage } = useMarketingImages();

  const title = getProductFeature(feature, 'title');
  const description = getProductFeature(feature, 'description');
  const alt = getProductFeature(feature, 'alt');
  const bullets = getProductFeatureBullets(feature);

  return (
    <motion.section
      className={`group relative overflow-hidden py-24 ${reverse ? 'bg-slate-800/50' : 'bg-gradient-to-br from-slate-900 to-slate-800'}`}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.8 }}
    >
      {/* Dynamic Background Elements */}
      <motion.div
        className="absolute right-0 top-0 size-72 rounded-full bg-primary-500/5 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: reverse ? 0 : 4,
        }}
      />
      <motion.div
        className="absolute bottom-0 left-0 size-64 rounded-full bg-primary-600/5 blur-2xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: reverse ? 4 : 0,
        }}
      />

      <div className="container relative z-10 mx-auto px-6">
        <div
          className={`flex flex-col items-center gap-16 ${
            reverse ? 'md:flex-row-reverse' : 'md:flex-row'
          }`}
        >
          <motion.div
            className="w-full md:w-1/2"
            initial={{ opacity: 0, x: reverse ? 50 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.h3
              className="mb-6 text-3xl font-bold leading-[1.2] text-white md:text-4xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {title}
            </motion.h3>
            <motion.p
              className="mb-8 text-xl leading-relaxed text-slate-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {description}
            </motion.p>
            <motion.ul
              className="space-y-4"
              role="list"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {bullets.map((bullet, i) => (
                <motion.li
                  key={i}
                  className="group flex cursor-pointer items-center"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.6 + i * 0.1 }}
                  whileHover={{ x: 5 }}
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CheckCircle2
                      className="mr-4 size-6 text-green-400 transition-colors group-hover:text-green-300"
                      aria-hidden="true"
                    />
                  </motion.div>
                  <span className="text-lg text-slate-300 transition-colors group-hover:text-white">
                    {bullet}
                  </span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div
            className="w-full md:w-1/2"
            initial={{ opacity: 0, x: reverse ? -50 : 50, scale: 0.9 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
          >
            <motion.div
              className="group relative overflow-hidden rounded-2xl"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <motion.img
                src={getImage(feature)}
                alt={alt}
                loading="lazy"
                className="w-full rounded-2xl border border-slate-700/50 object-cover shadow-2xl transition-all duration-500 group-hover:border-primary-500/30"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.5 }}
              />
              <motion.div
                className="absolute inset-0 rounded-2xl bg-gradient-to-t from-primary-500/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                initial={false}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default function ProductPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    getNavigationText,
    getProductHero,
    getProductIntegrations,
    getProductCTA,
    getLandingFooter,
    getIntegrationPlatforms,
  } = useMarketingTranslations();

  // Clear any stale authentication state when visiting product page
  // React.useEffect(() => {
  //   clearAuthState()
  // }, []) // Empty dependency array - only run once on mount

  const handleRequestDemo = () => {
    setIsModalOpen(true);
  };

  // Structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'KernLogic PIM',
    description:
      'Advanced PIM platform with data import, flexible attribute management, advanced analytics, and seamless integrations',
    url: 'https://kernlogic.com/marketing/product',
    applicationCategory: 'ProductInformationManagement',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      url: 'https://kernlogic.com/marketing/pricing',
    },
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://kernlogic.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Features',
        item: 'https://kernlogic.com/marketing/product',
      },
    ],
  };

  // Get structured data from translations
  const integrationPlatforms = getIntegrationPlatforms();

  return (
    <main
      role="main"
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      data-scroll-container
    >
      {/* SEO Meta Tags */}
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>

      {/* Navigation */}
      <motion.header
        className="sticky top-0 z-50 border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm"
        role="banner"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <nav
          className="container mx-auto px-6 py-4"
          role="navigation"
          aria-label={getNavigationText('ariaLabel')}
        >
          <div className="flex items-center justify-between">
            <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
              <Link to="/marketing" className="group flex items-center">
                <motion.img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt={getNavigationText('brand')}
                  className="size-12 transition-transform group-hover:scale-105"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
                <span className="ml-3 text-xl font-bold leading-[1.2] text-white transition-colors group-hover:text-primary-300">
                  {getNavigationText('brand')}
                </span>
              </Link>
            </motion.div>
            <div className="flex items-center space-x-6">
              <motion.div
                className="hidden items-center space-x-6 md:flex"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <motion.div whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
                  <Link
                    to="/marketing/product"
                    className="rounded-md px-3 py-2 text-sm font-medium text-primary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    tabIndex={0}
                    aria-current="page"
                  >
                    {getNavigationText('product')}
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
                  <Link
                    to="/marketing/pricing"
                    className="rounded-md px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    tabIndex={0}
                  >
                    {getNavigationText('pricing')}
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
                  <Link
                    to="/login"
                    className="rounded-md px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    tabIndex={0}
                  >
                    {getNavigationText('login')}
                  </Link>
                </motion.div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <LanguageToggle />
              </motion.div>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Page Hero/Intro */}
      <section
        className="group relative z-10 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 py-20 text-center lg:py-32"
        aria-labelledby="hero-title"
      >
        {/* Ambient Radial Glow */}
        <motion.div
          className="absolute left-1/2 top-[45%] z-0 size-[480px] -translate-x-1/2 rounded-full bg-primary-500/10 opacity-70 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Additional Dynamic Elements */}
        <motion.div
          className="absolute right-[10%] top-[20%] size-32 rounded-full bg-primary-400/5 blur-2xl"
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
        <motion.div
          className="absolute bottom-[30%] left-[5%] size-24 rounded-full bg-primary-600/5 blur-xl"
          animate={{
            y: [0, 25, 0],
            x: [0, -15, 0],
            scale: [1, 1.4, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />

        {/* Background Illustration */}
        <motion.div
          className="pointer-events-none absolute left-[16%] top-[1%] z-0 hidden -translate-x-1/2 sm:block"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5 }}
        >
          <motion.img
            src="/Product Features Graphics.png"
            alt="Abstract Feature Background"
            className="pointer-events-none w-[800px] max-w-none select-none 
            opacity-[0.06] blur-sm transition-all duration-700 
            ease-in-out group-hover:opacity-10 group-hover:blur-[1px] lg:w-[850px] xl:w-[1000px]"
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>

        <div className="container mx-auto px-6">
          <div className="relative z-10 mx-auto max-w-4xl text-center">
            <motion.h1
              id="hero-title"
              className="mb-8 bg-gradient-to-r from-white via-slate-100 to-slate-300 bg-clip-text py-2 text-5xl font-bold leading-[1.1] text-transparent md:text-6xl"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              {getProductHero('featureOverviewTitle')}
            </motion.h1>
            <motion.p
              className="mx-auto mb-12 max-w-3xl text-xl leading-relaxed text-slate-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            >
              {getProductHero('featureOverviewSubtitle')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Feature Deep-Dive Sections */}
      <FeatureSection feature="dataImport" />
      <FeatureSection feature="attributeFamily" reverse />
      <FeatureSection feature="bulkOperations" />
      <FeatureSection feature="searchFilter" reverse />
      <FeatureSection feature="analytics" />

      {/* Integration Logos */}
      <section
        className="relative overflow-hidden bg-slate-800/50 py-24"
        aria-labelledby="integrations-title"
      >
        {/* Dynamic Background */}
        <motion.div
          className="bg-gradient-radial absolute left-1/2 top-1/2 size-96 -translate-x-1/2 -translate-y-1/2 rounded-full from-primary-500/10 to-transparent"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
        />

        <div className="container relative z-10 mx-auto px-6 text-center">
          <motion.h2
            id="integrations-title"
            className="mb-12 py-1 text-4xl font-bold leading-[1.2] text-white md:text-5xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {getProductIntegrations('title')}
          </motion.h2>
          <motion.div
            className="flex flex-wrap items-center justify-center gap-12 lg:gap-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {integrationPlatforms.slice(0, 3).map((platform, idx) => {
              // Map platform names to actual connector icons
              const iconMap: Record<string, string> = {
                Shopify: '/connectoricon/shopify.png',
                Magento: '/connectoricon/magento.png',
                WooCommerce: '/connectoricon/bigcommerce.png', // Using BigCommerce icon for WooCommerce
              };

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30, scale: 0.8 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.6,
                    delay: 0.1 * idx,
                    type: 'spring',
                    bounce: 0.4,
                  }}
                  whileHover={{
                    scale: 1.1,
                    y: -5,
                    transition: { duration: 0.2 },
                  }}
                  className="group cursor-pointer"
                >
                  <motion.img
                    src={
                      iconMap[platform.name] ||
                      `/images/logos/${platform.name.toLowerCase().replace(/\s+/g, '-')}.webp`
                    }
                    alt={platform.alt}
                    loading="lazy"
                    className="h-16 w-auto opacity-70 grayscale transition-all duration-500 group-hover:opacity-100 group-hover:grayscale-0"
                    whileHover={{ rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.5 }}
                  />
                </motion.div>
              );
            })}

            {/* Custom Connector */}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.8 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: 0.3,
                type: 'spring',
                bounce: 0.4,
              }}
              whileHover={{
                scale: 1.1,
                y: -5,
                transition: { duration: 0.2 },
              }}
              className="group flex cursor-pointer flex-col items-center gap-2"
            >
              <motion.img
                src="/kernlogic/kernlogic-logo-icon.svg"
                alt={getProductIntegrations('customAlt')}
                loading="lazy"
                className="h-16 w-auto opacity-70 grayscale transition-all duration-500 group-hover:opacity-100 group-hover:grayscale-0"
                whileHover={{ rotate: [0, -5, 5, 0] }}
                transition={{ duration: 0.5 }}
              />
              <span className="text-sm font-medium text-slate-400 transition-colors duration-300 group-hover:text-primary-400">
                {getProductIntegrations('custom')}
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="relative overflow-hidden bg-gradient-to-r from-primary-600 to-primary-500 py-20"
        aria-labelledby="cta-title"
      >
        {/* Dynamic Background Pattern */}
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '30px 30px',
          }}
        />

        {/* Floating Elements */}
        <motion.div
          className="absolute right-10 top-10 size-20 rounded-full bg-white/5"
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-10 left-10 size-16 rounded-full bg-white/5"
          animate={{
            y: [0, 15, 0],
            x: [0, -8, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />

        <div className="container relative z-10 mx-auto px-6">
          <div className="mx-auto max-w-4xl text-center">
            <motion.h2
              id="cta-title"
              className="mb-6 py-2 text-4xl font-bold leading-[1.1] text-white md:text-5xl"
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, type: 'spring', bounce: 0.3 }}
            >
              {getProductCTA('title')}
            </motion.h2>
            <motion.p
              className="mb-10 text-xl leading-relaxed text-primary-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {getProductCTA('subtitle')}
            </motion.p>

            <motion.div
              className="flex flex-col items-center justify-center gap-6 sm:flex-row"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  onClick={handleRequestDemo}
                  size="lg"
                  className="group h-auto border border-slate-700 bg-slate-900 px-8 py-4 text-lg text-white shadow-xl transition-all duration-200 hover:bg-slate-800 focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 focus:ring-offset-primary-600"
                >
                  {getProductCTA('requestDemo')}
                  <motion.div
                    className="ml-3 inline-block"
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <ArrowRight
                      className="size-5 text-white transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </motion.div>
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="group h-auto border-2 border-white/30 bg-white/10 px-8 py-4 text-lg text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/20 focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-primary-600"
                >
                  <Link to="/marketing/pricing">
                    {getProductCTA('seePricing')}
                    <motion.div
                      className="ml-3 inline-block"
                      animate={{ x: [0, 3, 0] }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        delay: 0.5,
                      }}
                    >
                      <ArrowRight
                        className="size-5 transition-transform group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </motion.div>
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 bg-slate-900 py-16" role="contentinfo">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
            {/* Company Info */}
            <div>
              <h4 className="mb-6 text-xl font-semibold text-white">
                {getLandingFooter('companyInfo')}
              </h4>
              <p className="mb-6 text-slate-400">
                {getLandingFooter('copyright', {
                  year: new Date().getFullYear(),
                })}
              </p>
            </div>

            {/* Empty middle column */}
            <div></div>

            {/* Links */}
            <div>
              <h4 className="mb-6 text-xl font-semibold text-white">
                {getLandingFooter('quickLinks')}
              </h4>
              <ul className="space-y-3 text-slate-400" role="list">
                <li>
                  <Link
                    to="/marketing"
                    className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                  >
                    {getLandingFooter('links.home')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/marketing/product"
                    className="rounded-md px-2 py-1 font-medium text-primary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    aria-current="page"
                  >
                    {getLandingFooter('links.features')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/marketing/pricing"
                    className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                  >
                    {getLandingFooter('links.pricing')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                  >
                    {getLandingFooter('links.login')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 border-t border-slate-700/50 pt-8 text-center text-slate-500">
            <Link
              to="/privacy"
              className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getLandingFooter('legal.privacy')}
            </Link>
            <span className="mx-2">·</span>
            <Link
              to="/terms"
              className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getLandingFooter('legal.terms')}
            </Link>
            <span className="mx-2">·</span>
            <Link
              to="/cookies"
              className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getLandingFooter('legal.cookies')}
            </Link>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
}
